import React, { useEffect, useState } from "react";
import 'bootstrap/dist/css/bootstrap.min.css'; // Import Bootstrap CSS
import Topbar from "../../components/Topbar";
import Sidebar from "../../components/Sidebar";

// Default image URL
const DEFAULT_AVATAR_URL = "https://i.pinimg.com/736x/34/60/3c/34603ce8a80b1ce9a768cad7ebf63c56.jpg";

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form fields state
  const [newName, setNewName] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [token, setToken] = useState("")
  
  // API call state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(sessionStorage.getItem("admin"));
    if (storedUser) {
      setUser(storedUser);
      setNewName(storedUser.fullname || "");
      // Do not pre-fill password fields for security
    }
  }, []);

  useEffect(()=>{
    const token = sessionStorage.getItem("token")
    setToken(token)
    
  })
  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  const resetPasswordFields = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
    clearMessages();
    if (user && !isEditing) { // When switching to edit mode
      setNewName(user.fullname); // Reset newName to current fullname
      resetPasswordFields(); // Clear password fields
    } else if (isEditing) { // When cancelling edit mode
      resetPasswordFields(); // Also clear password fields on cancel
    }
  };

  const handleSave = async () => {
    clearMessages();

    if (newName.trim() === "") {
      setError("Full name cannot be empty.");
      return;
    }

    const payload = {
      fullname: newName.trim(),
      email: user.email, // Assuming email is part of the user object and might be needed by API
    };

    // Password change logic
    if (newPassword) {
      if (!currentPassword) {
        setError("Please enter your current password to set a new one.");
        return;
      }
      if (newPassword !== confirmNewPassword) {
        setError("New passwords do not match.");
        return;
      }
      // Add more password strength validation if needed
      if (newPassword.length < 6) { // Example: min length
        setError("New password must be at least 6 characters long.");
        return;
      }
      payload.currentPassword = currentPassword;
      payload.newPassword = newPassword;
    } else if (currentPassword && !newPassword) {
      // User entered current password but not new password
      setError("Please enter a new password if you wish to change it, or clear the current password field if you only want to update your name.");
      return;
    }


    // Check if there are any actual changes to submit
    const noNameChange = newName.trim() === user.fullname;
    const noPasswordChange = !newPassword; // if newPassword field is empty

    if (noNameChange && noPasswordChange) {
      setIsEditing(false);
      setSuccessMessage("No changes were made.");
      resetPasswordFields();
      return;
    }


    setIsLoading(true);

    // Retrieve token - adjust 'user.token' if your token is stored differently
    // For example, if token is stored directly: const token = sessionStorage.getItem("yourTokenKey");
    ; // Assuming the token is part of the user object

    if (!token && (payload.newPassword || payload.currentPassword)) { // Or for any protected action
      setError("Authentication token not found. Please log in again.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_APIURL}/api/update-admin`,
        {
          method: "PUT", // Or "PATCH"
          headers: {
            "Content-Type": "application/json",
            ...(token && { "Authorization": `Bearer ${token}` }) // Add Authorization header if token exists
          },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.message || "Failed to update profile. Please check your inputs.");
      }

      const updatedUser = { ...user, fullname: newName.trim() };
      // If password was changed successfully, the backend might not return the new token or password.
      // Session storage update here is primarily for the name.
      // For security, do not store passwords in state or session storage after they are used.
      setUser(updatedUser);
      sessionStorage.setItem("admin", JSON.stringify(updatedUser)); // Be careful what you store back
      setIsEditing(false);
      setSuccessMessage("Profile updated successfully!");
      resetPasswordFields(); // Clear password fields after successful save

    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!user && !JSON.parse(sessionStorage.getItem("admin"))) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) return <p className="text-center mt-5">User data not found. Please log in again.</p>;

  const profileImageUrl = user.photo
    ? `${process.env.REACT_APP_APIURL}${user.photo}`
    : DEFAULT_AVATAR_URL;

  return (

    <>
      <Topbar />
      <Sidebar />
      <div className="container mt-4 mb-5" style={{
        paddingTop:"100px"
      }}>
        <div className="row d-flex justify-content-center">
          <div className="col-md-8 col-lg-7 col-xl-6">
            <div className="card shadow-lg border-0 rounded-3">
              <div className="card-header bg-primary text-white text-center p-3">
                <h4 className="mb-0">User Admin Profile</h4>
              </div>
              <div className="card-body p-4">
                <div className="text-center mb-4">
                  <img
                    src={profileImageUrl}
                    alt="User Avatar"
                    className="rounded-circle img-thumbnail"
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      border: "4px solid #dee2e6"
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = DEFAULT_AVATAR_URL;
                    }}
                  />
                </div>

                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {successMessage && <div className="alert alert-success" role="alert">{successMessage}</div>}

                <div className="mb-3">
                  <label htmlFor="email" className="form-label fw-bold">Email:</label>
                  <input
                    type="email"
                    id="email"
                    className="form-control-plaintext"
                    value={user.email}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", padding: ".375rem .75rem", borderRadius: ".25rem" }}
                  />
                </div>

                <div className="mb-3">
                  <label htmlFor="fullname" className="form-label fw-bold">Full Name:</label>
                  {isEditing ? (
                    <input
                      type="text"
                      id="fullname"
                      className="form-control"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <div className="form-control-plaintext" style={{ backgroundColor: "#f8f9fa", padding: ".375rem .75rem", borderRadius: ".25rem" }}>
                      {user.fullname}
                    </div>
                  )}
                </div>

                {isEditing && (
                  <>
                    <hr className="my-4" />
                    <h5 className="mb-3">Change Password (optional)</h5>
                    <div className="mb-3">
                      <label htmlFor="currentPassword" className="form-label fw-bold">Current Password:</label>
                      <input
                        type="password"
                        id="currentPassword"
                        className="form-control"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        autoComplete="current-password"
                      />
                      <small className="form-text text-muted">Required if changing password.</small>
                    </div>
                    <div className="mb-3">
                      <label htmlFor="newPassword" className="form-label fw-bold">New Password:</label>
                      <input
                        type="password"
                        id="newPassword"
                        className="form-control"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                      />
                    </div>
                    <div className="mb-3">
                      <label htmlFor="confirmNewPassword" className="form-label fw-bold">Confirm New Password:</label>
                      <input
                        type="password"
                        id="confirmNewPassword"
                        className="form-control"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                    </div>
                  </>
                )}

                <div className="mt-4 d-flex justify-content-end gap-2">
                  {isEditing ? (
                    <>
                      <button
                        className="btn btn-success"
                        onClick={handleSave}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          "Save Changes"
                        )}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={handleToggleEdit}
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-primary" onClick={handleToggleEdit}>
                      Edit Profile
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default UserProfile;